import React from 'react';
import { Table, AlertTriangle, Info } from 'lucide-react';
import type { CSVPreviewData } from '../types';

interface PreviewDataProps {
  previewData: CSVPreviewData;
}

const PreviewData: React.FC<PreviewDataProps> = ({ previewData }) => {
  const { columns, rows, totalRows } = previewData;
  const previewRows = rows.slice(0, 5); // Show first 5 rows
  const willImportRows = Math.min(totalRows, 1000);
  const exceedsLimit = totalRows > 1000;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Preview Data
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Review the first 5 records from your CSV file before proceeding with column mapping.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <Table className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Total Rows</p>
          </div>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalRows}</p>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <Info className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm font-medium text-green-900 dark:text-green-100">Will Import</p>
          </div>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{willImportRows}</p>
        </div>

        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <Table className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <p className="text-sm font-medium text-purple-900 dark:text-purple-100">Columns</p>
          </div>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{columns.length}</p>
        </div>
      </div>

      {/* Warning if exceeds limit */}
      {exceedsLimit && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Row Limit Exceeded</p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Your CSV contains {totalRows} rows, but only the first 1000 rows will be imported.
              The remaining {totalRows - 1000} rows will be skipped.
            </p>
          </div>
        </div>
      )}

      {/* Preview Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                  #
                </th>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
              {previewRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {rowIndex + 1}
                  </td>
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap max-w-xs truncate"
                      title={row[column]}
                    >
                      {row[column] || <span className="text-gray-400 dark:text-gray-600">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Message */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold">Note:</span> This is a preview of the first 5 rows. In the next step,
          you'll map these columns to your CRM fields before importing.
        </p>
      </div>

      {/* Column List */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Detected Columns ({columns.length})
        </h4>
        <div className="flex flex-wrap gap-2">
          {columns.map((column, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full border border-blue-200 dark:border-blue-800"
            >
              {column}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreviewData;
